import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { createProductUrl } from '~/components/CategoryBlocks/utils';
import Icon from '~/components/Icon/Icon';
import { IconTypes } from '~/components/Icon/IconTypes';
import { SommelierWindow } from '~/components/SommelierWindow';
import { ProductClickEventDetail } from '~/components/SommelierWindow/interfaces';
import { getProductSlugFromSommelierEvent } from '~/components/SommelierWindow/utils';
import { useGlobal } from '~/hooks/useGlobal';

import styles from './SommelierButton.module.scss';

const SommelierButton = ({
  onClick,
  className,
  icon,
}: {
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  icon?: IconTypes;
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { isSSR, isMobile } = useGlobal();
  const navigate = useNavigate();
  const buttonRef = useRef<Nullable<HTMLButtonElement>>(null);
  const modalWrapperRef = useRef<Nullable<HTMLDivElement>>(null);

  const handleToggleSommelierWindow = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    e.stopPropagation();
    setIsModalOpen((prev) => !prev);
  };

  const handleCloseSommelierWindow = () => {
    setIsModalOpen(false);
  };

  const handleSommelierProductClick = (
    detail: ProductClickEventDetail,
    e: Event,
  ) => {
    e.stopPropagation();
    const slug = getProductSlugFromSommelierEvent(detail);

    if (!slug) {
      return;
    }

    // TODO: Handle analytics event
    handleCloseSommelierWindow();
    navigate(createProductUrl({ id: slug }));
  };

  const renderModal = () => {
    if (isSSR) {
      return null;
    }

    return createPortal(
      <SommelierWindow
        open={isModalOpen}
        onProductClick={handleSommelierProductClick}
        ref={modalWrapperRef}
        className={styles.window}
        onClose={handleCloseSommelierWindow}
      />,
      document.body,
    );
  };

  useEffect(() => {
    const removeClassName = () => {
      document.body.classList.remove(`${styles.noScroll}`);
    };
    const outsideClickListener = (e: Event) => {
      const targetNode = e.target as HTMLElement;

      if (targetNode.tagName === 'TEXTAREA') {
        targetNode.focus();
        return;
      }

      // Buttons in the suggestions block have unpredictable behaviour
      // .contains() method return false even for document.body element.
      // Added extra check for these buttons
      if (
        targetNode.tagName === 'BUTTON' &&
        targetNode.parentElement?.classList.contains('sb-footer-suggestions')
      ) {
        return;
      }

      if (
        buttonRef.current?.contains(targetNode) ||
        modalWrapperRef.current?.contains(targetNode)
      ) {
        return;
      }

      handleCloseSommelierWindow();
    };
    const cleanUp = () => {
      window.removeEventListener('click', outsideClickListener);
    };

    removeClassName();
    cleanUp();
    if (isModalOpen && isMobile) {
      document.body.classList.add(`${styles.noScroll}`);
    }
    window.addEventListener('click', outsideClickListener);

    return () => {
      removeClassName();
      cleanUp();
    };
  }, [isModalOpen, isMobile]);

  return (
    <>
      <button
        ref={buttonRef}
        className={classes([styles.genieButton, className])}
        onClick={handleToggleSommelierWindow}
      >
        {t('header:askGenie')}
        {icon && <Icon type={icon} size={16} />}
      </button>

      {renderModal()}
    </>
  );
};

export default observer(SommelierButton);
